import {
  Account,
  Activity,
  Animal,
  Job,
  Image,
  Mind,
  Support,
  Skill,
  Help,
  Feedback,
  Chat,
  Item,
  Manual,
  Document,
  Memory,
  Note,
  Device,
  Notification,
  User,
  PrintOrder,
  Merchandise,
  CreatePersonaTask,
  CreateImageTask,
  HandleJobTask,
  UpdateBundle,
  PublicAnimal,
  Goal,
  Metric,
  JobsDefinition,
  CrewRolesDefinition,
  getAccountReference,
  Definition,
  BatchOperation,
  BackupData,
  Redirect,
  Event,
  Craft,
} from ".";
import { WholesalerPurchase } from "./purchases";
import { Blog } from "./blogs";
import type { CraftType } from "./craftTypes";
import { Performance } from "./performances";
import type { StoryDraft } from "./storyDraft";
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  Timestamp,
  snapshotExists
} from "@um/contracts/firestore";
import { z } from "zod";
import { Settings } from "./settings";
import { TestResourceGroup } from "./TestResources";
import { getFirestoreContractProvider } from "./FirestoreContractProvider";

export type WithId<T = DocumentData> = T & {
  id: string;
};

export type WithRef<T = DocumentData> = T & {
  ref: DocumentReference<T>;
};

export type WithAccount<T = DocumentData> = WithRef<T> & {
  account: WithRef<Account>;
};

export type WithRefOf<T> = T extends WithRef | WithRef[]
  ? T
  : T extends CollectionReference<infer D>
    ? WithRef<D>
    : T extends DocumentReference<infer D>
      ? WithRef<D>
      : T extends Array<DocumentReference<infer D>>
        ? Array<WithRef<D>>
        : never;

export type DocumentReferenceOf<T> = T extends DocumentReference | DocumentReference[]
  ? T
  : T extends Array<WithRef<infer D>>
    ? Array<DocumentReference<D>>
    : T extends WithRef<infer D>
      ? DocumentReference<D>
      : never;

export function withRef<T = DocumentData>(snapshot: DocumentSnapshot<T>): WithRef<T> {
  const data = snapshot.data() as T;
  // business logic for handing back compat with old data, remove when not needed
  const backCompat = data as any;
  if (backCompat?.dynamicContentResponse && !backCompat.response) {
    backCompat.response = JSON.parse(backCompat?.dynamicContentResponse);
  }

  return {
    ref: snapshot.ref,
    ...data,
  };
}

export function withRefOrUndefined<T = DocumentData>(snapshot: DocumentSnapshot<T>): WithRef<T> | undefined {
  if (!snapshotExists(snapshot)) {
    return undefined;
  }
  return withRef<T>(snapshot);
}

export interface DatabaseRecord {
  created?: Timestamp;
  updated?: Timestamp;
  imported?: Timestamp;
}

export interface DBType<T = DocumentData, P = void> {
  name: string;
  description?: string;
}

function dbType<T = DocumentData, P = void>(def: { name: string; description?: string }): DBType<T, P> {
  return {
    name: def.name,
    description: def.description,
  };
}

export const dbTypes = {
  // Account
  Account: dbType<Account>({ name: "Account" }),
  Event: dbType<Event, Account>({ name: "Event" }),
  Craft: dbType<Craft, Account>({ name: "Craft" }),
  Animal: dbType<Animal, Account>({ name: "Animal" }),
  Job: dbType<Job, Account>({ name: "Job" }),
  Activity: dbType<Activity, Account>({ name: "Activity" }),
  Support: dbType<Support, Account>({ name: "Support" }),
  Skill: dbType<Skill, Account>({ name: "Skill" }),
  Help: dbType<Help, Account>({ name: "Help" }),
  Feedback: dbType<Feedback, Account>({ name: "Feedback" }),
  HandleJobTask: dbType<HandleJobTask, Account>({ name: "HandleJobTask" }),
  Chat: dbType<Chat, Account>({ name: "Chat" }),
  Item: dbType<Item, Account>({ name: "Item" }),
  Manual: dbType<Manual, Account>({ name: "Manual" }),
  Memory: dbType<Memory, Account>({ name: "Memory" }),
  Note: dbType<Note, Account>({ name: "Note" }),
  Document: dbType<Document, Account>({ name: "Document" }),
  Mind: dbType<Mind, Account>({ name: "Mind" }),
  Image: dbType<Image, Account>({ name: "Image" }),
  Goal: dbType<Goal, Account>({ name: "Goal" }),
  Metric: dbType<Metric, Account>({ name: "Metric" }),
  PrintOrder: dbType<PrintOrder, Account>({ name: "PrintOrder" }),
  Merchandise: dbType<Merchandise, Account>({ name: "Merchandise" }),
  Purchase: dbType<WholesalerPurchase, Account>({ name: "Purchase" }),
  Settings: dbType<Settings, Account>({ name: "Settings" }),
  Performance: dbType<Performance, Account>({ name: "Performance" }),
  StoryDraft: dbType<StoryDraft, Account>({ name: "StoryDraft" }),

  // User
  User: dbType<User>({ name: "User" }),
  Notification: dbType<Notification, User>({ name: "Notification" }),
  Device: dbType<Device, User>({ name: "Device" }),

  // Infrastructure
  CreateImageTask: dbType<CreateImageTask>({ name: "CreateImageTask" }),
  CreatePersonaTask: dbType<CreatePersonaTask>({ name: "CreatePersonaTask" }),

  // Admin / Audit
  BatchOperation: dbType<BatchOperation>({ name: "BatchOperation" }),
  BackupData: dbType<BackupData, BatchOperation>({ name: "BackupData" }),
  Redirect: dbType<Redirect>({ name: "Redirect" }),
  Blog: dbType<Blog>({ name: "Blog" }),
  CraftType: dbType<CraftType>({ name: "CraftType" }),

  // App: to be defined
  Definition: dbType<Definition>({ name: "Definition" }),
  UpdateBundle: dbType<UpdateBundle>({ name: "UpdateBundle" }),
};

const zRootSchema = z.object({
  accounts: z.custom<CollectionReference<Account>>().describe("Account"),
  users: z.custom<CollectionReference<User>>().describe("User"),
  createImageTasks: z.custom<CollectionReference<CreateImageTask>>().describe("CreateImageTask"),
  createPersonaTasks: z.custom<CollectionReference<CreatePersonaTask>>().describe("CreatePersonaTask"),
  updateBundles: z.custom<CollectionReference<UpdateBundle>>().describe("UpdateBundle"),
  testResourceGroups: z.custom<CollectionReference<TestResourceGroup>>().describe("TestResourceGroup"),
  batchOperations: z.custom<CollectionReference<BatchOperation>>().describe("BatchOperation"),
  redirects: z.custom<CollectionReference<Redirect>>().describe("Redirect"),
  blogs: z.custom<CollectionReference<Blog>>().describe("Blog"),
  craftTypes: z.custom<CollectionReference<CraftType>>().describe("CraftType"),
});

const zUserSchema = z.object({
  notifications: z.custom<CollectionReference<Notification>>().describe("Notification"),
  devices: z.custom<CollectionReference<Device>>().describe("Device"),
});

export const zAccountSchema = z.object({
  events: z.custom<CollectionReference<Event>>().describe("Event"),
  crafts: z.custom<CollectionReference<Craft>>().describe("Craft"),
  minds: z.custom<CollectionReference<Mind>>().describe("Mind"),
  animals: z.custom<CollectionReference<Animal>>().describe("Animal"),
  jobs: z.custom<CollectionReference<Job>>().describe("Job"),
  activities: z.custom<CollectionReference<Activity>>().describe("Activity"),
  supports: z.custom<CollectionReference<Support>>().describe("Support"),
  skills: z.custom<CollectionReference<Skill>>().describe("Skill"),
  helps: z.custom<CollectionReference<Help>>().describe("Help"),
  feedback: z.custom<CollectionReference<Feedback>>().describe("Feedback"),
  chats: z.custom<CollectionReference<Chat>>().describe("Chat"),
  items: z.custom<CollectionReference<Item>>().describe("Item"),
  manuals: z.custom<CollectionReference<Manual>>().describe("Manual"),
  memories: z.custom<CollectionReference<Memory>>().describe("Memory"),
  notes: z.custom<CollectionReference<Note>>().describe("Note"),
  documents: z.custom<CollectionReference<Document>>().describe("Document"),
  images: z.custom<CollectionReference<Image>>().describe("Image"),
  goals: z.custom<CollectionReference<Goal>>().describe("Goal"),
  metrics: z.custom<CollectionReference<Metric>>().describe("Metric"),
  printOrders: z.custom<CollectionReference<PrintOrder>>().describe("PrintOrder"),
  merchandise: z.custom<CollectionReference<Merchandise>>().describe("Merchandise"),
  purchases: z.custom<CollectionReference<WholesalerPurchase>>().describe("Purchase"),
  settings: z.custom<CollectionReference<Settings>>().describe("Settings"),
  performances: z.custom<CollectionReference<Performance>>().describe("Performance"),
  handleJobTasks: z.custom<CollectionReference<HandleJobTask>>().describe("HandleJobTask"),

  // HACK: for Job onNew handling.  There may be a better way to do this
  accountUsers: z.custom<CollectionReference<User>>().describe("*User"), // * means that the paths to be provided at runtime using allDocs
  accountDevices: z.custom<CollectionReference<Device>>().describe("*Device"),
});

export type AccountSchema = z.infer<typeof zAccountSchema>;

export function getSchemaRelationship(
  schema: z.SomeZodObject,
  collection: CollectionReference
): keyof z.infer<typeof schema> | undefined {
  return Object.entries(schema.shape).find(([key, value]) => value.description === collection.id)?.[0];
}

function getFirestoreSchema<T extends z.SomeZodObject>(schema: T, parent?: DocumentReference): z.infer<T> {
  return Object.fromEntries(
    Object.entries(schema.shape).map(([key, value]) => [key, getFirestoreContractProvider().getCollection(value.description!, parent)])
  );
}

export function getAccountSchema(account: WithRef | DocumentReference | string): z.infer<typeof zAccountSchema> {
  const accountRef = getAccountReference(account);
  return getFirestoreSchema(zAccountSchema, accountRef);
}

export const getSchema = lazy(() => {
  const rootSchema = getFirestoreSchema(zRootSchema);
  return {
    ...rootSchema,
    public: {
      account: getFirestoreContractProvider().getDocument("public", rootSchema.accounts),
      ...(getAccountSchema(getFirestoreContractProvider().getDocument("public", rootSchema.accounts)) as Omit<
        z.infer<typeof zAccountSchema>,
        "animals"
      > & {
        animals: CollectionReference<PublicAnimal>;
      }),
    },
    apps: {
      uniqueminds: {
        definitions: {
          jobs: getFirestoreContractProvider().getDocument<JobsDefinition>("App/uniqueminds/Definition/jobs"),
          crewRoles: getFirestoreContractProvider().getDocument<CrewRolesDefinition>("App/uniqueminds/Definition/crewRoles"),
        },
      },
    },
  };
});

function lazy<T>(fn: () => T): () => T {
  let value: T | undefined;
  return () => {
    if (value === undefined) {
      value = fn();
    }
    return value;
  };
}

// create a zod schema that can be used for reference types
export function zTimestamp() {
  return z
    .preprocess((input) => {
      if (typeof input === "string") {
        return new Date(input);
      }
      return input && input instanceof Timestamp ? input.toDate() : input;
    }, z.date())
    .transform((date) => {
      return Timestamp.fromDate(date);
    });
}

export function zReferenceTo<T = DocumentData>(type: DBType<T, Account>, description: string) {
  return (
    z
      .preprocess((input) => {
        // to allow for both DocumentReference and string round trip references to a string
        return input && input instanceof DocumentReference ? input.path : input;
      }, z.string())
      .transform((path, ctx) => {
        // Convert string path to DocumentReference
        try {
          return getFirestoreContractProvider().getDocument<T>(path);
        } catch (error) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid document reference path: '${path}'`,
          });
          return z.NEVER;
        }
      })
      // add the type name to the description so that we can communicate this reference type in JSON Schema
      .describe(`[${type.name}] ${description}`)
  );
}

export function zWithRefTo<T = DocumentData>(type: DBType<T, Account>, description: string) {
  return (
    z
      .preprocess((input) => {
        // to allow for both DocumentReference and string round trip references to a string
        return input && input instanceof DocumentReference ? input.path : input;
      }, z.string())
      .transform(async (path, ctx) => {
        // Convert string path to DocumentReference
        try {
          return await getFirestoreContractProvider().getWithRef<T>(path);
        } catch (error) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid document reference path: '${path}'`,
          });
          return z.NEVER;
        }
      })
      // add the type name to the description so that we can communicate this reference type in JSON Schema
      .describe(`[${type.name}] ${description}`)
  );
}
